/**
 * Playing a score from a page: the audio context, the click, the count-in and the loop.
 *
 * It is a hook rather than page code because all of it hangs together -- the context can
 * only be made by a gesture, the metronome and the count-in are options the player is
 * rebuilt with, and the loop is a stretch of seconds worked out from the selection -- and
 * because a second board that wants to play a score wants exactly this.
 *
 * Playing starts from the selection when there is one, which is what a player asking to hear
 * "from here" means. With nothing selected it carries on from wherever the position stands.
 *
 * The engine is a {@link SampledEngine}, so parts are heard on recordings of the instruments
 * that play them. Those have to be fetched and decoded before the first note, which is why
 * pressing play can wait: it is a second at most, once, and it is the difference between
 * hearing an arrangement and hearing a row of oscillators.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { playFrom, rangeOf } from "@app/score-editor";
import type { AudioEngine, Score, Selection, TimeRange } from "@app/score-editor";
import { usePlayback } from "@app/score-editor/react";
import type { Playback } from "@app/score-editor/react";
import { useSampledEngine, useSamplesFor } from "./use-sampled-engine";

export interface ScoreSound {
  readonly playback: Playback;
  readonly metronome: boolean;
  readonly countIn: boolean;
  /** Whether the articulations, hairpins, ornaments and fermatas are played as well. */
  readonly marks: boolean;
  readonly loop: TimeRange | null;
  /**
   * The recordings for this score are still being fetched, and play is waiting for them.
   *
   * Worth showing: the wait is short but it is the one moment pressing play does nothing
   * visible, and a transport that looks broken for a second is a transport nobody trusts.
   */
  readonly loading: boolean;
  toggleMetronome(): void;
  toggleCountIn(): void;
  toggleMarks(): void;
  /** Play from the selection, or pause what is playing. */
  playPause(): void;
  stop(): void;
  /** Repeat the selected passage, or stop repeating. */
  toggleLoop(): void;
  /**
   * The audio engine, made if it does not exist yet and woken if it is asleep.
   *
   * A browser will only let a page make sound after a gesture, so there is exactly one
   * context on the board and this is the door to it: whoever acts on a gesture -- pressing
   * play, or attaching a MIDI keyboard -- calls this, and everything else is heard through
   * what it answers with. A second context would play the same score twice over.
   */
  wake(): AudioEngine;
}

export function useScoreSound(score: Score, selection: Selection): ScoreSound {
  const { engine: audio, wake } = useSampledEngine();
  const [metronome, setMetronome] = useState(false);
  const [countIn, setCountIn] = useState(false);
  const [marks, setMarks] = useState(true);

  const playback = usePlayback(audio, score, {
    volume: 0.5,
    metronome,
    countInBars: countIn ? 1 : 0,
    marks,
  });

  // The context is created by the same click that starts the music, which is the gesture a
  // browser wants to see. Until then `usePlayback` still answers with a schedule, so the
  // length of the piece and the position can be shown before any sound is possible, and
  // the recordings it names can be fetched the moment there is a context to decode into.
  const waiting = useRef(false);
  const recorded = useSamplesFor(audio, playback.schedule);

  const from = useCallback(
    (): number | undefined =>
      selection.events.size === 0 ? undefined : playFrom(playback.schedule, selection.events),
    [playback.schedule, selection.events],
  );

  const playPause = useCallback((): void => {
    if (playback.playing) {
      playback.pause();
      return;
    }
    wake();
    if (audio === null || !recorded) {
      waiting.current = true;
      return;
    }
    playback.play(from());
  }, [audio, from, playback, recorded, wake]);

  useEffect(() => {
    if (audio === null || !recorded || !waiting.current) return;
    waiting.current = false;
    playback.play(from());
  }, [audio, from, playback, recorded]);

  const toggleLoop = useCallback((): void => {
    if (playback.loop !== null) {
      playback.setLoop(null);
      return;
    }
    playback.setLoop(rangeOf(playback.schedule, selection.events));
  }, [playback, selection.events]);

  return {
    playback,
    metronome,
    countIn,
    marks,
    loop: playback.loop,
    loading: audio !== null && !recorded,
    toggleMetronome: useCallback(() => setMetronome((on) => !on), []),
    toggleCountIn: useCallback(() => setCountIn((on) => !on), []),
    toggleMarks: useCallback(() => setMarks((on) => !on), []),
    playPause,
    stop: playback.stop,
    toggleLoop,
    wake,
  };
}
